import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email.trim() || !form.message.trim()) return toast.error("Add your email and a message");
        setSending(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            toast.success("Message sent — thanks for reaching out!");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send");
        } finally { setSending(false); }
    };

    return (
        <div className="max-w-xl mx-auto px-4 md:px-8 py-12" data-testid="contact-page">
            <div className="flex items-center gap-2 mb-2">
                <Mail className="h-7 w-7 text-[#ff4500]" />
                <h1 className="font-display text-4xl sm:text-5xl tracking-wide">Contact Us</h1>
            </div>
            <p className="text-sm text-[#a0a0a0] mb-8">Questions, corrections, or partnership ideas? Send us a note and we'll get back to you.</p>

            {sent ? (
                <div className="border border-[#22c55e]/40 bg-[#22c55e]/10 p-6 text-center" data-testid="contact-success">
                    <div className="font-display text-2xl text-[#22c55e]">Message sent</div>
                    <p className="text-sm text-[#cfcfcf] mt-2">Thanks — we've received your message and will reply to {form.email}.</p>
                    <button className="mt-4 text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }} data-testid="contact-send-another">Send another</button>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Name">
                            <input data-testid="contact-name" value={form.name} onChange={set("name")} maxLength={120} className="w-full h-11 bg-[#141414] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
                        </Field>
                        <Field label="Email" required>
                            <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} required className="w-full h-11 bg-[#141414] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
                        </Field>
                    </div>
                    <Field label="Subject">
                        <input data-testid="contact-subject" value={form.subject} onChange={set("subject")} maxLength={160} className="w-full h-11 bg-[#141414] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
                    </Field>
                    <Field label="Message" required>
                        <textarea data-testid="contact-message" value={form.message} onChange={set("message")} required rows={6} maxLength={5000} className="w-full bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#ff4500] resize-y" />
                    </Field>
                    <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#ff4500] hover:bg-[#e03e00] text-white px-6 h-11 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-40">
                        <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send message"}
                    </button>
                </form>
            )}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}{required && <span className="text-[#ff4500] ml-0.5">*</span>}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
